import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, AlertCircle, QrCode, Flashlight, SwitchCamera, RotateCcw } from 'lucide-react';
import { useWorkingQRScan, type ProductData } from '@/hooks/use-working-qr-scan';

interface WorkingQRScanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductFound: (product: ProductData) => void;
  title?: string;
}

export function WorkingQRScanModal({
  open,
  onOpenChange,
  onProductFound,
  title = "QR Kod ile Ürün Ekle"
}: WorkingQRScanModalProps) {
  
  const {
    isScanning,
    hasPermission,
    flashEnabled,
    cameras,
    currentCameraId,
    error,
    companyId,
    authLoading,
    videoRef,
    startScanning,
    stopScanning,
    toggleFlash,
    switchCamera
  } = useWorkingQRScan((product) => {
    onProductFound(product);
    onOpenChange(false);
  });

  // Don't auto-start scanning - require manual user interaction
  useEffect(() => {
    if (!open) {
      stopScanning();
    }
  }, [open, stopScanning]);

  // Stop scanning when modal closes
  useEffect(() => {
    if (!open) {
      stopScanning();
    }
  }, [open, stopScanning]);

  const handleClose = () => {
    stopScanning();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              {title}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <DialogDescription>
            Ürün QR kodunu kamera ile tarayarak hızlıca ekleyin
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera Permission Alert */}
          {hasPermission === false && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Kamera İzni Gerekli</p>
                  <p className="text-xs text-red-700 mt-1">
                    QR kod tarayabilmek için kamera erişim izni vermeniz gerekiyor.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-center">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Scanner Area */}
          <div className="relative mx-auto w-80 h-80 border-2 border-gray-300 rounded-2xl overflow-hidden bg-black">
            {/* Video Element for Camera Feed */}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover ${isScanning ? 'block' : 'hidden'}`}
              playsInline
              muted
              autoPlay
              webkit-playsinline="true"
              style={{
                transform: currentCameraId.includes('front') || currentCameraId.includes('user') ? 'scaleX(-1)' : 'scaleX(1)',
                WebkitTransform: currentCameraId.includes('front') || currentCameraId.includes('user') ? 'scaleX(-1)' : 'scaleX(1)'
              }}
            />

            {/* Camera Icon When Not Scanning */}
            {!isScanning && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                    <Camera className="text-blue-600" size={40} />
                  </div>
                  <p className="text-sm text-gray-600">
                    {authLoading ? "Hazırlanıyor..." : "Kamerayı Başlatın"}
                  </p>
                </div>
              </div>
            )}

            {/* Scanning Overlay */}
            {isScanning && (
              <div className="absolute inset-4 pointer-events-none">
                {/* Scanning Frame */}
                <div className="relative w-full h-full border-2 border-green-400 rounded-2xl">
                  {/* Corner Markers */}
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                  
                  {/* Scanning Animation Line */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-green-400 animate-pulse rounded-full"></div>
                  
                  {/* Center Crosshair */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 border-2 border-green-400 rounded-full animate-ping"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          {isScanning && (
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFlash}
                className="flex-col gap-1 h-16"
              >
                <Flashlight className={`w-4 h-4 ${flashEnabled ? "text-yellow-500" : ""}`} />
                <span className="text-xs">{flashEnabled ? "Flaş Kapat" : "Flaş Aç"}</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={switchCamera}
                disabled={cameras.length <= 1}
                className="flex-col gap-1 h-16"
              >
                <SwitchCamera className="w-4 h-4" />
                <span className="text-xs">Kamera</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  stopScanning();
                  setTimeout(startScanning, 100);
                }}
                className="flex-col gap-1 h-16"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="text-xs">Yenile</span>
              </Button>
            </div>
          )}

          {/* Instructions */}
          <div className="text-center">
            {authLoading ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Hazırlanıyor...</p>
                <p className="text-xs text-gray-600">Kullanıcı bilgileri yükleniyor</p>
              </div>
            ) : hasPermission === false ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600">Kamera İzni Gerekli</p>
                <p className="text-xs text-gray-600">Tarayıcı ayarlarından kamera iznini açın</p>
              </div>
            ) : error ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600">Kamera Hatası</p>
                <p className="text-xs text-gray-600">{error}</p>
              </div>
            ) : isScanning ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-green-600">QR Kod Aranıyor...</p>
                <p className="text-xs text-gray-600">QR kodu kamera ile tarayın</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Hazır</p>
                <p className="text-xs text-gray-600">Kamerayı başlatmak için aşağıdaki butona basın</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
            >
              İptal
            </Button>
            <Button
              variant={isScanning ? "destructive" : "default"}
              className="flex-1"
              onClick={async () => {
                if (isScanning) {
                  stopScanning();
                } else {
                  // Ensure camera access happens in direct response to user gesture
                  await startScanning();
                }
              }}
              disabled={hasPermission === false || !!error || authLoading || !companyId}
            >
              {authLoading ? (
                "Yükleniyor..."
              ) : isScanning ? (
                "Durdur"
              ) : (
                "Kamerayı Başlat"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
